import { useState, type FormEvent } from 'react'
import {
  CAlert,
  CButton,
  CButtonGroup,
  CForm,
  CFormCheck,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { consoleApi, apiErrorMessage } from '../../api/client'
import { ErrorState, LoadingState } from '../../components/common/AsyncState'
import { PageHeader } from '../../components/common/PageHeader'
import { formatDate } from '../../utils/format'

const projectInitial = {
  alias: '',
  title: '',
  summary: '',
  category: '',
  status: 'active',
  public: true,
}
const serviceInitial = {
  slug: '',
  name: '',
  publicDescription: '',
  status: 'unknown',
  statusMessage: '',
  public: true,
  launchUrl: '',
  healthUrl: '',
  requiredGroups: '',
  owner: '',
}
const researchInitial = {
  title: '',
  type: 'paper',
  authors: '',
  venue: '',
  publishedAt: '',
  url: '',
  doi: '',
  summary: '',
  projectAlias: '',
  usageStart: '',
  usageEnd: '',
  featured: true,
  public: true,
}

type Tab = 'projects' | 'services' | 'research'
export function CatalogPage() {
  const client = useQueryClient()
  const [tab, setTab] = useState<Tab>('projects')
  const [modal, setModal] = useState(false)
  const [project, setProject] = useState(projectInitial)
  const [service, setService] = useState(serviceInitial)
  const [research, setResearch] = useState(researchInitial)
  const projects = useQuery({
    queryKey: ['console', 'catalog', 'projects'],
    queryFn: consoleApi.projects,
  })
  const services = useQuery({
    queryKey: ['console', 'catalog', 'services'],
    queryFn: consoleApi.adminServices,
  })
  const outputs = useQuery({
    queryKey: ['console', 'catalog', 'research'],
    queryFn: consoleApi.adminResearch,
  })
  const createProject = useMutation({
    mutationFn: consoleApi.createProject,
    onSuccess: async () => {
      setModal(false)
      setProject(projectInitial)
      await client.invalidateQueries({ queryKey: ['console', 'catalog', 'projects'] })
    },
  })
  const createService = useMutation({
    mutationFn: consoleApi.createService,
    onSuccess: async () => {
      setModal(false)
      setService(serviceInitial)
      await client.invalidateQueries({ queryKey: ['console', 'catalog', 'services'] })
    },
  })
  const createResearch = useMutation({
    mutationFn: consoleApi.createResearch,
    onSuccess: async () => {
      setModal(false)
      setResearch(researchInitial)
      await client.invalidateQueries({ queryKey: ['console', 'catalog', 'research'] })
    },
  })
  const error = createProject.error ?? createService.error ?? createResearch.error
  const submitProject = (event: FormEvent) => {
    event.preventDefault()
    createProject.mutate(project)
  }
  const submitService = (event: FormEvent) => {
    event.preventDefault()
    createService.mutate({
      ...service,
      ...(service.statusMessage ? { statusMessage: service.statusMessage } : {}),
      ...(service.launchUrl ? { launchUrl: service.launchUrl } : {}),
      ...(service.healthUrl ? { healthUrl: service.healthUrl } : {}),
      requiredGroups: service.requiredGroups
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean),
      ...(service.owner ? { owner: service.owner } : {}),
    })
  }
  const submitResearch = (event: FormEvent) => {
    event.preventDefault()
    createResearch.mutate({
      ...research,
      authors: research.authors
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      publishedAt: research.publishedAt ? new Date(research.publishedAt).toISOString() : null,
      usageStart: research.usageStart ? new Date(research.usageStart).toISOString() : null,
      usageEnd: research.usageEnd ? new Date(research.usageEnd).toISOString() : null,
      ...(research.venue ? { venue: research.venue } : {}),
      ...(research.url ? { url: research.url } : {}),
      ...(research.doi ? { doi: research.doi } : {}),
      ...(research.projectAlias ? { projectAlias: research.projectAlias } : {}),
    })
  }
  const current = tab === 'projects' ? projects : tab === 'services' ? services : outputs
  return (
    <>
      <PageHeader
        eyebrow="Administrator controls"
        title="Public & internal catalogues"
        description="Manage public project aliases, service policies and research outputs. Sensitive URLs and group rules stay inside the authenticated API."
        actions={
          <CButton color="primary" onClick={() => setModal(true)}>
            Add {tab === 'projects' ? 'project' : tab === 'services' ? 'service' : 'research output'}
          </CButton>
        }
      />
      <div className="filter-row filter-row--console">
        <CButtonGroup>
          <CButton color={tab === 'projects' ? 'primary' : 'light'} onClick={() => setTab('projects')}>
            Projects
          </CButton>
          <CButton color={tab === 'services' ? 'primary' : 'light'} onClick={() => setTab('services')}>
            Services
          </CButton>
          <CButton color={tab === 'research' ? 'primary' : 'light'} onClick={() => setTab('research')}>
            Research outputs
          </CButton>
        </CButtonGroup>
      </div>
      {error && <CAlert color="danger">{apiErrorMessage(error)}</CAlert>}
      {current.isLoading && <LoadingState />}
      {current.isError && <ErrorState message={apiErrorMessage(current.error)} />}
      {tab === 'projects' && projects.data && (
        <div className="catalog-grid">
          {projects.data.map((item) => (
            <article key={item._id}>
              <div>
                <code>{item.alias}</code>
                <span className={`catalog-visibility ${item.public ? 'public' : 'internal'}`}>
                  {item.public ? 'public' : 'internal'}
                </span>
              </div>
              <h2>{item.title}</h2>
              <p>{item.summary}</p>
              <footer>
                <span>{item.category}</span>
                <strong>{item.status}</strong>
              </footer>
            </article>
          ))}
        </div>
      )}
      {tab === 'services' && services.data && (
        <div className="catalog-grid">
          {services.data.map((item) => (
            <article key={item._id}>
              <div>
                <code>{item.slug}</code>
                <span className={`service-status service-status-${item.status}`}>{item.status}</span>
              </div>
              <h2>{item.name}</h2>
              <p>{item.publicDescription}</p>
              <footer>
                <span>{item.owner ?? 'No owner'}</span>
                <strong>{item.public ? 'Public status' : 'Internal'}</strong>
              </footer>
            </article>
          ))}
        </div>
      )}
      {tab === 'research' && outputs.data && (
        <div className="catalog-grid">
          {outputs.data.map((item) => (
            <article key={item._id}>
              <div>
                <span className="category">{item.type}</span>
                {item.projectAlias && <code>{item.projectAlias}</code>}
              </div>
              <h2>{item.title}</h2>
              <p>{item.summary}</p>
              <footer>
                <span>
                  {item.venue ?? 'No venue'} · {formatDate(item.publishedAt)}
                </span>
                <strong>{item.featured ? 'Featured' : 'Listed'}</strong>
              </footer>
            </article>
          ))}
        </div>
      )}
      <CModal visible={modal} onClose={() => setModal(false)} size="lg">
        <CModalHeader>
          <CModalTitle>
            Add{' '}
            {tab === 'projects' ? 'project alias' : tab === 'services' ? 'service' : 'research output'}
          </CModalTitle>
        </CModalHeader>
        {tab === 'projects' ? (
          <CForm onSubmit={submitProject}>
            <CModalBody>
              <div className="form-grid">
                <label>
                  Public alias
                  <CFormInput
                    value={project.alias}
                    onChange={(event) => setProject({ ...project, alias: event.target.value })}
                    placeholder="LR-024"
                    required
                  />
                </label>
                <label>
                  Category
                  <CFormInput
                    value={project.category}
                    onChange={(event) => setProject({ ...project, category: event.target.value })}
                    required
                  />
                </label>
                <label className="form-grid__full">
                  Title
                  <CFormInput
                    value={project.title}
                    onChange={(event) => setProject({ ...project, title: event.target.value })}
                    required
                  />
                </label>
                <label className="form-grid__full">
                  Public summary
                  <CFormTextarea
                    rows={4}
                    value={project.summary}
                    onChange={(event) => setProject({ ...project, summary: event.target.value })}
                    required
                  />
                </label>
                <label>
                  Status
                  <CFormSelect
                    value={project.status}
                    onChange={(event) => setProject({ ...project, status: event.target.value })}
                  >
                    <option value="planned">Planned</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </CFormSelect>
                </label>
                <label className="check-label">
                  <CFormCheck
                    checked={project.public}
                    onChange={(event) => setProject({ ...project, public: event.target.checked })}
                  />{' '}
                  Public catalogue entry
                </label>
              </div>
            </CModalBody>
            <CModalFooter>
              <CButton color="light" onClick={() => setModal(false)}>
                Cancel
              </CButton>
              <CButton color="primary" type="submit">
                Create project
              </CButton>
            </CModalFooter>
          </CForm>
        ) : tab === 'services' ? (
          <CForm onSubmit={submitService}>
            <CModalBody>
              <div className="form-grid">
                <label>
                  Slug
                  <CFormInput
                    value={service.slug}
                    onChange={(event) => setService({ ...service, slug: event.target.value })}
                    placeholder="model-inference"
                    required
                  />
                </label>
                <label>
                  Status
                  <CFormSelect
                    value={service.status}
                    onChange={(event) => setService({ ...service, status: event.target.value })}
                  >
                    <option value="unknown">Unknown</option>
                    <option value="operational">Operational</option>
                    <option value="degraded">Degraded</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="offline">Offline</option>
                  </CFormSelect>
                </label>
                <label className="form-grid__full">
                  Name
                  <CFormInput
                    value={service.name}
                    onChange={(event) => setService({ ...service, name: event.target.value })}
                    required
                  />
                </label>
                <label className="form-grid__full">
                  Public description
                  <CFormTextarea
                    rows={3}
                    value={service.publicDescription}
                    onChange={(event) =>
                      setService({ ...service, publicDescription: event.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  Launch URL
                  <CFormInput
                    type="url"
                    value={service.launchUrl}
                    onChange={(event) => setService({ ...service, launchUrl: event.target.value })}
                  />
                </label>
                <label>
                  Health-check URL
                  <CFormInput
                    type="url"
                    value={service.healthUrl}
                    onChange={(event) => setService({ ...service, healthUrl: event.target.value })}
                  />
                </label>
                <label className="form-grid__full">
                  Required FreeIPA group DNs (comma or newline separated)
                  <CFormTextarea
                    rows={3}
                    value={service.requiredGroups}
                    onChange={(event) => setService({ ...service, requiredGroups: event.target.value })}
                  />
                </label>
                <label>
                  Owner
                  <CFormInput
                    value={service.owner}
                    onChange={(event) => setService({ ...service, owner: event.target.value })}
                  />
                </label>
                <label className="check-label">
                  <CFormCheck
                    checked={service.public}
                    onChange={(event) => setService({ ...service, public: event.target.checked })}
                  />{' '}
                  Show public status
                </label>
              </div>
              <div className="form-note">
                <strong>SSRF protection</strong>
                <p>The hostname in a health-check URL must also appear in HEALTHCHECK_ALLOWED_HOSTS.</p>
              </div>
            </CModalBody>
            <CModalFooter>
              <CButton color="light" onClick={() => setModal(false)}>
                Cancel
              </CButton>
              <CButton color="primary" type="submit">
                Create service
              </CButton>
            </CModalFooter>
          </CForm>
        ) : (
          <CForm onSubmit={submitResearch}>
            <CModalBody>
              <div className="form-grid">
                <label>
                  Type
                  <CFormSelect
                    value={research.type}
                    onChange={(event) => setResearch({ ...research, type: event.target.value })}
                  >
                    <option value="paper">Paper</option>
                    <option value="thesis">Thesis</option>
                    <option value="model">Model</option>
                    <option value="dataset">Dataset</option>
                    <option value="software">Software</option>
                    <option value="demonstration">Demonstration</option>
                  </CFormSelect>
                </label>
                <label>
                  Project alias
                  <CFormInput
                    value={research.projectAlias}
                    onChange={(event) => setResearch({ ...research, projectAlias: event.target.value })}
                  />
                </label>
                <label className="form-grid__full">
                  Title
                  <CFormInput
                    value={research.title}
                    onChange={(event) => setResearch({ ...research, title: event.target.value })}
                    required
                  />
                </label>
                <label className="form-grid__full">
                  Authors (comma separated)
                  <CFormInput
                    value={research.authors}
                    onChange={(event) => setResearch({ ...research, authors: event.target.value })}
                    required
                  />
                </label>
                <label>
                  Venue
                  <CFormInput
                    value={research.venue}
                    onChange={(event) => setResearch({ ...research, venue: event.target.value })}
                  />
                </label>
                <label>
                  Published at
                  <CFormInput
                    type="datetime-local"
                    value={research.publishedAt}
                    onChange={(event) => setResearch({ ...research, publishedAt: event.target.value })}
                  />
                </label>
                <label>
                  Public URL
                  <CFormInput
                    type="url"
                    value={research.url}
                    onChange={(event) => setResearch({ ...research, url: event.target.value })}
                  />
                </label>
                <label>
                  DOI
                  <CFormInput
                    value={research.doi}
                    onChange={(event) => setResearch({ ...research, doi: event.target.value })}
                  />
                </label>
                <label className="form-grid__full">
                  Public summary
                  <CFormTextarea
                    rows={4}
                    value={research.summary}
                    onChange={(event) => setResearch({ ...research, summary: event.target.value })}
                    required
                  />
                </label>
                <label>
                  H100 usage start
                  <CFormInput
                    type="datetime-local"
                    value={research.usageStart}
                    onChange={(event) => setResearch({ ...research, usageStart: event.target.value })}
                  />
                </label>
                <label>
                  H100 usage end
                  <CFormInput
                    type="datetime-local"
                    value={research.usageEnd}
                    onChange={(event) => setResearch({ ...research, usageEnd: event.target.value })}
                  />
                </label>
                <label className="check-label">
                  <CFormCheck
                    checked={research.featured}
                    onChange={(event) => setResearch({ ...research, featured: event.target.checked })}
                  />{' '}
                  Feature on landing page
                </label>
                <label className="check-label">
                  <CFormCheck
                    checked={research.public}
                    onChange={(event) => setResearch({ ...research, public: event.target.checked })}
                  />{' '}
                  Public output
                </label>
              </div>
            </CModalBody>
            <CModalFooter>
              <CButton color="light" onClick={() => setModal(false)}>
                Cancel
              </CButton>
              <CButton color="primary" type="submit">
                Create output
              </CButton>
            </CModalFooter>
          </CForm>
        )}
      </CModal>
    </>
  )
}
